import type {
  Account,
  GcpDiskSnapshotProperties,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { FEATURES_SUPPORT_VERSIONS } from '@/configs/featureConfig';

import { GcpVmConfiguration } from './steps/gcp/gcpVmConfiguration';
import { GcpVmDiskConfiguration } from './steps/gcp/gcpVmDiskConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface GcpDiskConfigState {
  diskSettings: GcpDiskSnapshotProperties;
  keepOriginalTags: boolean;
  keepOriginalSettings: boolean;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  encryptionKey?: string;
}

export interface RestoreGcpVMState {
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  supportedInstalledVersion?: string;
  restoreAccount?: Account;
  vmName: string;
  machineType: string;
  networkName?: string;
  subnetName?: string;
  networkHostProject?: string;
  isSharedNetwork?: boolean;
  regionName?: string;
  keepOriginalTags: boolean;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  disksState: GcpDiskConfigState[];
  initialDiskStates: GcpDiskConfigState[];
  zone?: string;
  encryptionKeyForAllDisks?: string;
}

const restoreGcpVMInstanceFlow: Step<RestoreGcpVMState> = {
  name: 'SelectTargetAccountStep',
  Component: SelectTargetAccountStep,
  next: [
    {
      name: 'GcpVmConfiguration',
      Component: GcpVmConfiguration,
      next: [
        {
          name: 'DiskConfiguration',
          Component: GcpVmDiskConfiguration,
        },
      ],
    },
  ],
};

export const RestoreGcpVMWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const diskStates =
    snapshot.resource?.properties?.gcpVm?.disks?.map((disk) => ({
      diskSettings: {
        ...disk,
        diskName: `${disk.diskName ?? ''}-restored`,
      },
      diskId: disk.diskId ?? '',
      diskName: `${disk.diskName ?? ''}-restored`,
      diskType: disk.diskType ?? '',
      diskSizeBytes: disk.diskSizeBytes ?? 0,
      keepOriginalTags: true,
      keepOriginalSettings: true,
      initialTags: disk.tags || {},
      tags: disk.tags || {},
    })) ?? [];

  const machineType =
    snapshot.resource?.properties?.gcpVm?.machineType?.split('/')?.pop() || '';

  return (
    <Wizard<RestoreGcpVMState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreGcpVMInstanceFlow]}
      initialState={{
        snapshot,
        resourceId: resource.id,
        supportedCloudProviders: [Provider.Gcp],
        supportedInstalledVersion:
          FEATURES_SUPPORT_VERSIONS.GCP_RESTORE_ACCOUNT_RESTORE_ACCOUNT,
        keepOriginalTags: true,
        vmName: `${snapshot.resource?.properties?.gcpVm?.name ?? ''}-restored`,
        machineType,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        initialDiskStates: diskStates,
        disksState: diskStates,
        regionName: snapshot.resource?.properties?.gcpVm?.region || 'us-east1',
      }}
      title={`Restore GCP Virtual Machine`}
      stepperLabels={[
        'Restore account',
        'Virtual machine configuration',
        'Disk configuration',
      ]}
    />
  );
};
